import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, uploadFile } from "../lib/api";
import { useFormat } from "../contexts/PrefsContext";
import { Modal, CrudActions } from "../components/Crud";
import DatePicker, { DateTimePicker } from "../components/DatePicker";
import BreakdownChip from "../components/BreakdownChip";
import ProjectBreakdown from "../components/ProjectBreakdown";
import BackButton from "../components/BackButton";
import ClientMemory from "../components/ClientMemory";
import { clientDisplayName } from "../lib/clientName";
import { ArrowLeft, Sparkles, Plus, ChevronDown, ChevronRight } from "lucide-react";

const TABS = ["contact", "contracts", "payments", "projects", "documents"];

export default function ClientDetail() {
    const { id } = useParams();
    const [client, setClient] = useState(null);
    const [tab, setTab] = useState("contact");
    useEffect(() => { api.get("/clients").then(r => setClient(r.data.find(c => c.id === id))); }, [id]);

    if (!client) return <div className="p-6">Loading…</div>;

    const fullName = clientDisplayName(client);

    return (
        <div data-testid="client-detail-page">
            <BackButton to="/clients" label="All clients" />
            <div className="flex items-end justify-between gap-3 mb-5">
                <div>
                    <div className="font-script text-[var(--brand-orange)] text-xl">{client.email}</div>
                    <h1 className="font-serif text-4xl" data-testid="client-detail-name">{fullName}</h1>
                </div>
            </div>
            <ClientSummary clientId={id} />
            <ClientMemory clientId={id} clientName={fullName} />
            <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-thin">
                {TABS.map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`client-tab-${t}`}
                            className={`px-4 py-1.5 rounded-full text-sm capitalize transition ${
                                tab === t ? "brand-gradient text-white" : "btn-ghost"
                            }`}>{t}</button>
                ))}
            </div>
            {tab === "contact" && <ContactTab client={client} />}
            {tab === "contracts" && <ContractsTab clientId={id} />}
            {tab === "payments" && <PaymentsTab clientId={id} />}
            {tab === "projects" && <ProjectsTab clientId={id} clientName={fullName} />}
            {tab === "documents" && <DocumentsTab clientId={id} />}
        </div>
    );
}

function ContactTab({ client }) {
    const fields = [
        ["First name", client.first_name], ["Middle name", client.middle_name], ["Surname", client.surname],
        ["Phone", client.phone], ["Email", client.email], ["Address", client.address],
        ["Bank account", client.bank_account], ["Tax code", client.tax_code],
        ["Passport", client.passport], ["Driving licence", client.driving_licence],
        ["Resident/NI", client.resident_number],
    ];
    return (
        <div className="card-warm p-6 grid sm:grid-cols-2 gap-4" data-testid="tab-contact">
            {fields.map(([l, v]) => (
                <div key={l}>
                    <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">{l}</div>
                    <div className="text-sm">{v || "—"}</div>
                </div>
            ))}
            <div className="sm:col-span-2 pt-2 border-t border-[rgba(26,19,24,0.08)]">
                <button className="btn-ghost" data-testid="verify-document-btn">Upload verification document</button>
                <span className="text-xs text-[var(--text-ink)]/55 ml-3">(coming soon)</span>
            </div>
        </div>
    );
}

function useScoped(name, scopeKey, scopeValue) {
    const [items, setItems] = useState([]);
    const load = async () => { const { data } = await api.get(`/${name}`); setItems(data.filter(i => i[scopeKey] === scopeValue)); };
    useEffect(() => { load(); }, [name, scopeValue]); // eslint-disable-line
    const create = async (b) => { const { data } = await api.post(`/${name}`, { ...b, [scopeKey]: scopeValue }); setItems(x => [data, ...x]); return data; };
    const update = async (id, b) => { const { data } = await api.put(`/${name}/${id}`, b); setItems(x => x.map(i => i.id === id ? data : i)); };
    const remove = async (id) => { await api.delete(`/${name}/${id}`); setItems(x => x.filter(i => i.id !== id)); };
    return { items, create, update, remove };
}

function ContractsTab({ clientId }) {
    const { items, create, remove } = useScoped("contracts", "client_id", clientId);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", parties: "", scope: "", payment_terms: "", duration: "", extra: "" });
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);

    const generate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/ai/contract", form);
            setContent(data.content);
        } finally { setLoading(false); }
    };
    const save = async () => { await create({ ...form, content }); setOpen(false); setContent(""); };
    const downloadPdf = (c) => {
        // Escape both the title and content so user-supplied text can't
        // execute scripts in the print window. Use srcdoc via a Blob URL
        // instead of the deprecated document.write.
        const esc = (s) => String(s || "")
            .replace(/&/g, "&amp;").replace(/</g, "&lt;")
            .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(c.title)}</title></head><body><pre style="white-space:pre-wrap;font-family:Georgia,serif;padding:24px">${esc(c.content)}</pre><script>window.onload=()=>setTimeout(()=>window.print(),200)</script></body></html>`;
        const blob = new Blob([html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank");
        // Revoke the object URL once the window has had time to load it.
        if (w) setTimeout(() => URL.revokeObjectURL(url), 5000);
    };

    return (
        <div data-testid="tab-contracts">
            <button onClick={() => setOpen(true)} className="btn-primary inline-flex items-center gap-2 mb-4" data-testid="add-contract-btn">
                <Sparkles size={16} /> New AI contract
            </button>
            <div className="grid sm:grid-cols-2 gap-4">
                {items.map((c) => (
                    <div key={c.id} className="card-warm p-4">
                        <div className="flex justify-between items-start">
                            <div><div className="font-serif text-lg">{c.title}</div>
                                <div className="text-xs text-[var(--text-ink)]/60">{(c.created_at || "").slice(0, 10)}</div></div>
                            <CrudActions idBase={`contract-${c.id}`} onDelete={() => remove(c.id)} />
                        </div>
                        <div className="mt-2 text-sm whitespace-pre-wrap line-clamp-4">{c.content}</div>
                        <button onClick={() => downloadPdf(c)} className="btn-ghost text-xs mt-3" data-testid={`contract-pdf-${c.id}`}>Save as PDF / Sign</button>
                    </div>
                ))}
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title="AI Contract Writer">
                <div className="grid gap-3">
                    {[["title", "Contract title"], ["parties", "Parties"], ["scope", "Scope of work"],
                      ["payment_terms", "Payment terms"], ["duration", "Duration"], ["extra", "Additional notes"]].map(([k, l]) => (
                        <label key={k} className="text-sm">{l}
                            <textarea value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                rows={k === "scope" || k === "extra" ? 3 : 1}
                                data-testid={`contract-input-${k}`}
                                className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                        </label>
                    ))}
                    <button onClick={generate} disabled={loading} className="btn-primary inline-flex items-center gap-2" data-testid="contract-generate-btn">
                        <Sparkles size={16} /> {loading ? "Drafting…" : "Generate with AI"}
                    </button>
                    {content && (
                        <>
                            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={10}
                                data-testid="contract-preview"
                                className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] font-mono text-sm" />
                            <button onClick={save} className="btn-primary" data-testid="contract-save-btn">Save contract</button>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
}

function PaymentsTab({ clientId }) {
    const { fmtCurrency } = useFormat();
    const { items, create, update, remove } = useScoped("payments", "client_id", clientId);
    const [form, setForm] = useState({ description: "", amount: "", status: "pending", due_date: "" });
    const [editing, setEditing] = useState(null); // full payment doc while modal open
    const total = items.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    // "Today" in YYYY-MM-DD, computed once per render. A payment is overdue
    // when it has a due date in the past AND is not yet marked paid. We
    // never overwrite the stored status — this is a purely visual signal
    // so users can still keep a "pending" invoice pending after its due
    // date and see it flagged.
    const todayYmd = new Date().toISOString().slice(0, 10);
    const isOverdue = (p) => !!p.due_date && p.due_date < todayYmd && p.status !== "paid";
    const saveEdit = async () => {
        if (!editing) return;
        await update(editing.id, {
            description: editing.description || "",
            amount: editing.amount,
            status: editing.status || "pending",
            due_date: editing.due_date || "",
        });
        setEditing(null);
    };
    return (
        <div data-testid="tab-payments">
            <div className="card-warm p-5 mb-4 flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">Total invoiced</div>
                    <div className="font-serif text-3xl" data-testid="payments-total">{fmtCurrency(total)}</div>
                </div>
                <div className="text-sm text-[var(--text-ink)]/55">Xero integration · coming soon</div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); create(form); setForm({ description: "", amount: "", status: "pending", due_date: "" }); }}
                  className="card-warm p-4 grid sm:grid-cols-5 gap-2 mb-4" data-testid="payment-form">
                <input required placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       data-testid="payment-input-description" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <input required type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       data-testid="payment-input-amount" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })}
                            testid="payment-input-due" placeholder="Due date" />
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                        data-testid="payment-input-status" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]">
                    <option value="pending">Pending</option><option value="paid">Paid</option><option value="overdue">Overdue</option>
                </select>
                <button className="btn-primary" data-testid="payment-add-btn">Add</button>
            </form>
            <div className="space-y-2">
                {items.map((p) => {
                    const overdue = isOverdue(p);
                    return (
                        <div key={p.id}
                             className={`card-warm p-3 flex items-center gap-3 ${overdue ? "ring-1 ring-[var(--status-red)]/40" : ""}`}
                             data-testid={`payment-row-${p.id}`}>
                            <div className="flex-1">
                                <div>{p.description}</div>
                                {p.due_date && (
                                    <div className={`text-xs mt-0.5 ${overdue ? "text-[var(--status-red)]" : "text-[var(--text-ink)]/55"}`}
                                         data-testid={`payment-due-${p.id}`}>
                                        Due {p.due_date}
                                    </div>
                                )}
                            </div>
                            <div className="font-mono">{fmtCurrency(parseFloat(p.amount) || 0)}</div>
                            {overdue && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--status-red)]/15 text-[var(--status-red)] font-medium uppercase tracking-wide"
                                      data-testid={`payment-overdue-flag-${p.id}`}>
                                    Overdue
                                </span>
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-[var(--status-green)]/15 text-[var(--status-green)]" : "bg-[var(--status-amber)]/20 text-[#8a6500]"}`}>{p.status}</span>
                            <CrudActions idBase={`payment-${p.id}`}
                                         onEdit={() => setEditing({ ...p })}
                                         onDelete={() => window.confirm("Delete this payment?") && remove(p.id)} />
                        </div>
                    );
                })}
            </div>
            <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit payment">
                {editing && (
                    <form onSubmit={(e) => { e.preventDefault(); saveEdit(); }}
                          className="grid gap-3" data-testid="payment-edit-form">
                        <label className="text-sm">Description
                            <input required value={editing.description || ""}
                                   onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                   data-testid="payment-edit-description"
                                   className="mt-1 w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                        </label>
                        <label className="text-sm">Amount
                            <input required type="number" step="0.01" value={editing.amount ?? ""}
                                   onChange={(e) => setEditing({ ...editing, amount: e.target.value })}
                                   data-testid="payment-edit-amount"
                                   className="mt-1 w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                        </label>
                        <div className="text-sm">
                            <div className="mb-1">Due date</div>
                            <DatePicker value={editing.due_date || ""}
                                        onChange={(v) => setEditing({ ...editing, due_date: v })}
                                        testid="payment-edit-due" placeholder="Due date" />
                        </div>
                        <label className="text-sm">Status
                            <select value={editing.status || "pending"}
                                    onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                                    data-testid="payment-edit-status"
                                    className="mt-1 w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]">
                                <option value="pending">Unpaid</option>
                                <option value="paid">Paid</option>
                                <option value="overdue">Overdue</option>
                            </select>
                        </label>
                        <div className="flex justify-end gap-2 mt-2">
                            <button type="button" className="btn-ghost" onClick={() => setEditing(null)} data-testid="payment-edit-cancel">Cancel</button>
                            <button type="submit" className="btn-primary" data-testid="payment-edit-save">Save changes</button>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}

function ProjectsTab({ clientId, clientName }) {
    const { items, create, remove } = useScoped("projects", "client_id", clientId);
    const [form, setForm] = useState({ title: "", start_date: "", end_date: "" });
    return (
        <div data-testid="tab-projects">
            <form onSubmit={(e) => { e.preventDefault(); create({ ...form, client_name: clientName }); setForm({ title: "", start_date: "", end_date: "" }); }}
                  className="card-warm p-4 grid sm:grid-cols-4 gap-2 mb-4" data-testid="client-project-form">
                <input required placeholder="Project title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })}
                            testid="cd-prj-start" placeholder="Start date" />
                <DateTimePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })}
                                testid="cd-prj-end" placeholder="Due (date & time)" />
                <button className="btn-primary"><Plus size={14} className="inline" /> Add</button>
            </form>
            <div className="space-y-2">
                {items.map((p) => (
                    <ClientProjectRow key={p.id} p={p} remove={remove} />
                ))}
            </div>
        </div>
    );
}

function ClientProjectRow({ p, remove }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="card-warm p-3" data-testid={`client-project-row-${p.id}`}>
            <div className="flex items-center gap-3">
                <button onClick={() => setOpen(!open)} data-testid={`client-project-expand-${p.id}`}
                        className="text-[var(--text-ink)]/55 hover:text-[var(--brand-orange)]"
                        title={open ? "Hide breakdown" : "Show breakdown"}>
                    {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                </button>
                <Link to={`/projects/${p.id}`} className="flex-1 truncate hover:text-[var(--brand-purple)] transition" data-testid={`open-project-${p.id}`}>{p.title}</Link>
                <CrudActions idBase={`cproject-${p.id}`} onDelete={() => remove(p.id)} />
            </div>
            <div className="mt-2"><BreakdownChip scope="project" id={p.id} testidSuffix={`cprj-${p.id}`} /></div>
            {open && (
                <div className="mt-3 border-t border-[rgba(26,19,24,0.06)] pt-3" data-testid={`client-project-breakdown-${p.id}`}>
                    <ProjectBreakdown scope="project" scopeId={p.id} />
                </div>
            )}
        </div>
    );
}

function DocumentsTab({ clientId }) {
    const { items, create, remove } = useScoped("documents", "client_id", clientId);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");

    const onFile = async (e) => {
        const f = e.target.files[0]; if (!f) return;
        setError(""); setUploading(true); setProgress(0);
        const fd = new FormData(); fd.append("file", f);
        try {
            const up = await uploadFile("/upload", fd, {
                onProgress: (p) => setProgress(p),
            });
            await create({ name: up.name, mime: up.content_type, size: up.size, storage_path: up.path, url: up.url });
        } catch (err) {
            setError(err.friendly || "Upload failed. Please try again.");
        } finally {
            setUploading(false); setProgress(0);
            e.target.value = "";
        }
    };
    return (
        <div data-testid="tab-documents">
            <div className="card-warm p-4 mb-4">
                <input type="file" onChange={onFile} disabled={uploading} data-testid="document-upload-input" />
                <div className="text-xs text-[var(--text-ink)]/55 mt-1">Stored securely in Emergent Object Storage · up to 25 MB.</div>
                {uploading && (
                    <div className="mt-3" data-testid="document-upload-progress">
                        <div className="flex justify-between text-xs mb-1">
                            <span>{progress < 100 ? "Uploading…" : "Processing document…"}</span>
                            <span className="tabular-nums">{progress}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden">
                            <div className="h-full brand-gradient transition-all duration-200" style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                )}
                {error && (
                    <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2 break-words" data-testid="document-upload-error">
                        {error}
                    </div>
                )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                {items.map((d) => (
                    <div key={d.id} className="card-warm p-3 flex items-center gap-3">
                        <a href={d.url || d.data_url} target="_blank" rel="noreferrer" className="flex-1 truncate text-sm hover:underline">{d.name}</a>
                        <CrudActions idBase={`doc-${d.id}`} onDelete={() => remove(d.id)} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function ClientSummary({ clientId }) {
    const { fmtCurrency } = useFormat();
    const [stats, setStats] = useState({ active: 0, invoiced: 0, paid: 0, progress: 0 });
    useEffect(() => {
        (async () => {
            const [pj, py] = await Promise.all([api.get("/projects"), api.get("/payments")]);
            const myProjects = pj.data.filter(p => p.client_id === clientId);
            const myPayments = py.data.filter(p => p.client_id === clientId);
            const active = myProjects.filter(p => !p.archived).length;
            const invoiced = myPayments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
            const paid = myPayments.filter(p => p.status === "paid").reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
            const avg = myProjects.length ? Math.round(myProjects.reduce((s, p) => s + (parseInt(p.progress) || 0), 0) / myProjects.length) : 0;
            setStats({ active, invoiced, paid, progress: avg });
        })().catch(() => {});
    }, [clientId]);
    const cardCls = "card-warm p-4";
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 fade-up" data-testid="client-summary">
            <div className={cardCls}>
                <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">Active projects</div>
                <div className="font-serif text-3xl mt-1" style={{ color: "var(--brand-purple)" }}>{stats.active}</div>
            </div>
            <div className={cardCls}>
                <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">Total invoiced</div>
                <div className="font-serif text-3xl mt-1" style={{ color: "var(--brand-magenta)" }} data-testid="client-stat-invoiced">{fmtCurrency(stats.invoiced)}</div>
            </div>
            <div className={cardCls}>
                <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">Paid</div>
                <div className="font-serif text-3xl mt-1" style={{ color: "var(--status-green)" }} data-testid="client-stat-paid">{fmtCurrency(stats.paid)}</div>
            </div>
            <div className={cardCls}>
                <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">Avg progress</div>
                <div className="font-serif text-3xl mt-1" style={{ color: "var(--brand-orange)" }}>{stats.progress}%</div>
                <div className="h-1.5 mt-2 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden">
                    <div className="h-full brand-gradient" style={{ width: `${stats.progress}%` }} />
                </div>
            </div>
        </div>
    );
}

